/**
 * First-party content (blog) click beacon — the blog analogue of
 * `src/lib/social/click-beacon.ts`.
 *
 * Records an inbound blog-post read — only `{ slug, medium }` — into
 * `content_attribution` via `POST /api/content/track`. This is the first-party
 * "which post drew this reader" signal the content Strategist ranks by, and the
 * conversion-measurement layer for the whole content channel.
 *
 * CONSENT: rides "necessary" consent, NOT opt-in "analytics" — same basis as the
 * social beacon. It stores only the post slug + arrival medium on our own domain
 * (no identifier, no cross-site cookie, no PII). Firing it BEFORE the
 * analytics-consent gate is deliberate: cold organic readers rarely accept the
 * banner, and gating the click behind analytics consent is exactly why social
 * attribution once sat at ~1 row forever.
 *
 * Fires once per slug per browser session (sessionStorage dedupe); never throws.
 */

import type { ContentMedium } from "./attribution";

const BLOG_PATH_RE = /^\/(?:en|id)\/blog\/([a-z0-9][a-z0-9-]*)\/?$/;

/** Extract the blog post slug from a localed blog-reader path, else null. */
export function blogSlugFromPath(
	pathname: string | null | undefined,
): string | null {
	if (!pathname) return null;
	const m = BLOG_PATH_RE.exec(pathname);
	return m ? m[1] : null;
}

const OWNED_MEDIUMS: ReadonlySet<string> = new Set<ContentMedium>([
	"newsletter",
	"rss",
	"social",
]);

/**
 * Coarse arrival medium: an explicit owned-channel `utm_medium` wins; otherwise
 * an external referrer reads as `organic` (search / links) and no referrer as
 * `direct`. Precise organic-vs-social split is not needed for the conversion
 * loop — `utm_medium` already tags the owned channels we care to separate.
 */
export function classifyContentMedium(input: {
	utmMedium?: string | null;
	hasReferrer: boolean;
}): ContentMedium {
	const um = input.utmMedium?.trim().toLowerCase();
	if (um && OWNED_MEDIUMS.has(um)) return um as ContentMedium;
	return input.hasReferrer ? "organic" : "direct";
}

/**
 * Detect a blog-post landing and record the click (session-deduped). Returns the
 * detected `{ slug, medium }` so the caller can also persist it as first-touch
 * (for later signup/apply linkage); returns null when the path is not a blog
 * post.
 */
export function recordContentClick(input: {
	pathname: string | null | undefined;
	utmMedium?: string | null;
	hasReferrer: boolean;
}): { slug: string; medium: ContentMedium } | null {
	const slug = blogSlugFromPath(input.pathname);
	if (!slug) return null;
	const medium = classifyContentMedium({
		utmMedium: input.utmMedium,
		hasReferrer: input.hasReferrer,
	});
	if (typeof window === "undefined") return { slug, medium };
	try {
		const key = `content_click:${slug}`;
		if (window.sessionStorage.getItem(key)) return { slug, medium };
		window.sessionStorage.setItem(key, "1");
		void fetch("/api/content/track", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			keepalive: true,
			body: JSON.stringify({ slug, medium }),
		}).catch(() => {});
	} catch {
		// sessionStorage blocked / private mode — skip silently
	}
	return { slug, medium };
}
